import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { PhoneCall, ShieldCheck, User } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";

/**
 * /call/:id — Slice K · Public call resolver.
 *
 * Anyone with the Find2Near Call ID can land here and reach the owner
 * through a bridged call. Owner's real number is never rendered.
 * A future Twilio IVR will pick up on the "Call now" click and bridge the
 * two sides; for now we log the attempt.
 */
export default function CallResolve() {
  const { id } = useParams();
  const [data, setData]   = useState(null);
  const [error, setError] = useState(null);
  useSeo({ title: `Reach ${id} — Find2Near` });

  useEffect(() => {
    api.get(`/ivr/route/${id}`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Unknown call ID"));
  }, [id]);

  const attempt = async () => {
    try {
      const r = await api.post("/ivr/bridge", { call_id: id });
      const provider = r.data?.provider === "twilio" ? "" : " (mock)";
      alert(`Bridging call${provider} · SID: ${r.data?.call_sid || "—"}\nYour phone will ring now.`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't bridge the call";
      alert(msg);
      // Still log the attempt for the owner's audit trail.
      api.post("/call/log", { call_id: id, channel: "web", status: "attempted" }).catch(() => {});
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-700 to-emerald-900 text-white grid place-items-center p-6" data-testid="call-resolve-page">
      <div className="max-w-sm w-full rounded-3xl bg-white text-stone-900 p-6 text-center">
        <div className="w-20 h-20 rounded-full bg-emerald-100 grid place-items-center mx-auto">
          {data?.owner_picture
            ? <img src={data.owner_picture} alt="" className="w-20 h-20 rounded-full object-cover" />
            : <User size={36} className="text-emerald-700" />}
        </div>
        <div className="mt-3 heading font-black text-2xl" data-testid="call-owner-name">
          {data?.owner_name || (error ? "Not found" : "…")}
        </div>
        <div className="text-xs text-stone-500 mt-1 tracking-widest tabular-nums">CALL ID · {id}</div>

        {error && (
          <div className="mt-4 text-sm text-rose-600" data-testid="call-error">{error}</div>
        )}

        {data && data.ok && (
          <>
            <div className="mt-4 text-xs text-stone-500 inline-flex items-center gap-1">
              <ShieldCheck size={12} /> Bridged call to {data.forward_masked} — no numbers exposed
            </div>
            <button
              onClick={attempt}
              className="mt-4 w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 inline-flex items-center justify-center gap-2"
              data-testid="call-connect"
            >
              <PhoneCall size={16} /> Call now
            </button>
          </>
        )}

        {data && !data.ok && (
          <div className="mt-4 text-sm text-stone-600" data-testid="call-owner-nomobile">
            This user hasn&apos;t linked a phone number yet.
          </div>
        )}

        <div className="mt-4 text-[11px] text-stone-400">
          <Link to="/" className="underline hover:text-stone-600">← back to Find2Near</Link>
        </div>
      </div>
    </div>
  );
}
